/* eslint-disable arrow-body-style */
import Promise from 'bluebird';
import _ from 'underscore';
import { runJobsInParallelWithRateLimit } from '@bingads-webui-universal/parallel-job-with-rate-limit';
import { parseFileName } from '@bingads-webui-campaign-react/upload-file-picker';

// if folder creation fails, continue partial upload around failed folder
// mark failing folder with unique id;
export const INVALID_FOLDER_ID = -9999;

const UPLOAD_PARALLEL_RATE_LIMIT = 6;

/*
* Folder traversal functions
* Upload folder via input button results in an array of File objects
* with property webkitRelativePath that contains path of File relative
* to (and including) the selected folder.
* e.g. if I selected a folder A with subfolder B and file C.jpg is in B
* then B's webkitRelativePath is "A/B/C.jpg"
*
* More information can be found here: https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/webkitdirectory
* Some of these property names may be liable to changing as the webkit
* prefix is an artifact of this folder upload feature being originally
* implemented in Chrome and not having been standardized across all
* browsers initially.
*/

// Will return an array of folders that form the relative path
// e.g. for "A/B/C.jpg", returns ["A", "B"]
const getRelativeFoldersFromRelativePath = (relativePath) => {
  const pathArr = _.map(relativePath.split('/'), name => name.trim());

  pathArr.pop();

  return pathArr;
};


// Will return name of the parent folder directly containing the file
// e.g. from file with path "A/B/C.jpg", returns "B"
export const getFileFolderName = (file) => {
  const { webkitRelativePath } = file;
  const pathArr = _.map(webkitRelativePath.split('/'), name => name.trim());

  pathArr.pop();

  return pathArr.pop();
};

// Filters out all files in the files array for files with supported
// extension type (not handled by default by browser)
export const filterFilesByExtensions = ({ files, supportedExtensions }) => {
  const acceptedFiles = [];

  _.each(files, (file) => {
    const { ext } = parseFileName(file.name);
    if (ext && _.contains(supportedExtensions, ext)) {
      acceptedFiles.push(file);
    }
  });

  return acceptedFiles;
};

// For a list of files, will return a map of the folder hierarchy
// where an empty object - {} - indicates that the folder is a leaf
// node
// e.g. for ["A/B/C.jpg", "A/D/E.jpg", "A/F.png"] will return
// { A: { B: { C: {} }, D: { E: {} } } }
export const createFolderHierarchyMap = (files) => {
  const foldersToCreate = {};

  _.each(files, ({ webkitRelativePath }) => {
    const folders = getRelativeFoldersFromRelativePath(webkitRelativePath);
    let currFolderMap = foldersToCreate;

    _.each(folders, (folder) => {
      if (!currFolderMap[folder]) {
        currFolderMap[folder] = {};
      }
      currFolderMap = currFolderMap[folder];
    });
  });

  return foldersToCreate;
};

const createFolderWithErrorHandling = ({
  folderCreationErrors,
  createFolderAsync,
  folderName,
  parentFolderId,
  currentFolderId,
  originalFolderName,
  numberOfAttempts = 1,
}) => {
  return createFolderAsync({
    folderName,
    parentFolderId,
  })
    .catch((e) => {
    // for duplicate folder upload, rename as <original folder>-1
      if (parentFolderId === currentFolderId) {
        const errorCode = _.get(e, ['body', 'value', 0, 'Code'], null);

        if (errorCode === 'DuplicateEntity') {
          return createFolderWithErrorHandling({
            folderCreationErrors,
            createFolderAsync,
            folderName: `${originalFolderName}-${numberOfAttempts}`,
            parentFolderId,
            currentFolderId,
            originalFolderName,
            numberOfAttempts: numberOfAttempts + 1,
          });
        }
      }
      // allow other folder creation jobs to continue as we allow partial succeed
      folderCreationErrors.push(e);
      return INVALID_FOLDER_ID;
    });
};

const createFoldersByHierarchyInternal = ({
  folderMap,
  createFolderAsync,
  creationPromises,
  parentFolderName,
  folderCreationErrors,
  currentFolderId,
}) => {
  const folderNames = _.keys(folderMap);
  if (folderNames.length > 0) {
    _.each(folderNames, (folderName) => {
      const subFoldersMap = folderMap[folderName];
      const parentPromise = parentFolderName ? creationPromises[parentFolderName] : Promise.resolve(currentFolderId);

      // eslint-disable-next-line no-param-reassign
      creationPromises[folderName] = parentPromise.then((parentFolderId) => {
        // if parent folder failed to be created, skip trying to create child folder
        if (parentFolderId === INVALID_FOLDER_ID) {
          return INVALID_FOLDER_ID;
        }

        return createFolderWithErrorHandling({
          folderCreationErrors,
          createFolderAsync,
          folderName,
          parentFolderId,
          currentFolderId,
          originalFolderName: folderName,
        });
      });

      createFoldersByHierarchyInternal({
        folderMap: subFoldersMap,
        createFolderAsync,
        creationPromises,
        parentFolderName: folderName,
        folderCreationErrors,
      });
    });
  }
};

// For a given folderMap (in the format created by createFolderHierarchyMap)
// Will create folders using the provided method in the correct sequence
// e.g. if for file "A/B/C.jpg", createFolderHierarchyMap will generate map
// { A: { B: {} } }, createFoldersByHierarchy will create folders in this order:
// create folder A with parentId = null (null used for root in MT)
// create folder B with parentId = folder A's Id
export const createFoldersByHierarchy = ({
  folderMap,
  createFolderAsync,
  folderCreationErrors = [],
  currentFolderId,
}) => {
  const creationPromises = {};

  createFoldersByHierarchyInternal({
    folderMap,
    createFolderAsync,
    creationPromises,
    folderCreationErrors,
    parentFolderName: null,
    currentFolderId,
  });

  return Promise.all(_.values(creationPromises))
    .then(() => creationPromises);
};

// Creates a promise which will ensure all assets are uploaded
// in a rate limited manner, in which we will send AddAsset requests
// one by one with a maximum of 6 parallel requests at a time
export const createRateLimitedAssetUploadPromises = ({
  customizedFilesOptions = [],
  acceptedAssets,
  folderIdPromises,
  validateAssetAsync = () => Promise.resolve(true),
  uploadAssetAsync,
  assetsSkippedUpload = [],
  assetUploadErrors = [],
  readAssetAsync,
}) => {
  const processAssetJobPromises = [];
  const assetUploadJobs = [];

  _.each(acceptedAssets, (asset, index) => {
    const folderName = getFileFolderName(asset);
    const extraOptions = customizedFilesOptions[index] || {};

    const processAssetJobPromise = folderIdPromises[folderName].then((folderId) => {
      if (folderId === INVALID_FOLDER_ID) {
        assetsSkippedUpload.push(asset);
        return Promise.resolve();
      }

      return readAssetAsync(asset, extraOptions)
        .then((dataURL) => {
          const assetUploadJob = () => {
            return validateAssetAsync({
              dataURL,
              ...extraOptions,
            })
              .then((validatedResult) => {
                const isImageUpscaled = _.get(validatedResult, 'isImageUpscaled', false);
                const { nameWithoutExt } = parseFileName(asset.name);

                return uploadAssetAsync({
                  dataURL,
                  fileObject: asset,
                  nameWithoutExt,
                  folderId,
                  isImageUpscaled,
                  ...extraOptions,
                });
              });
          };
          assetUploadJobs.push(assetUploadJob);
        })
        .catch((e) => {
          assetUploadErrors.push(e);
        });
    });

    processAssetJobPromises.push(processAssetJobPromise);
  });

  return Promise.all(processAssetJobPromises).then(() => {
    return runJobsInParallelWithRateLimit({
      jobsToRateLimit: assetUploadJobs,
      maxJobsInParallel: UPLOAD_PARALLEL_RATE_LIMIT,
      handleJobError: e => assetUploadErrors.push(e),
    });
  });
};
